import cloneDeep from 'clone-deep'
import type { InputType } from 'config'
import { useEffect, useRef, useState } from 'react'
import { confirmable, createConfirmation, ReactConfirmProps } from 'react-confirm'
import { Modal } from 'stories/components'
import { InputConvert } from 'utils/convertor'
import { buildFormState } from 'utils/form'
import { RenderInput } from 'utils/RenderInput'
import { InputValidate } from 'utils/validator'

const DEFAULT_FORM_CLASS_NAME = 'w-196 grid gap-4 md:grid-cols-2 grid-cols-1 text-sm'

export interface IInputsFormOption {
  title?: string
  inputs: Record<string, InputType>
  values?: Record<string, any>
  titleOkay?: string
  titleCancel?: string
  /** Stacking when opened from another modal (e.g. `2` from inputs-table). */
  childLevel?: number
  /** Grid wrapper class for form fields. Defaults to two-column on md+. */
  formClassName?: string
  showHistory?: (key: string) => void
}

interface InputsFormProps extends ReactConfirmProps {
  options: IInputsFormOption
}

const InputsFormDialog = ({ show, proceed, options }: InputsFormProps) => {
  const [lastUpdatedAt, setLastUpdatedAt] = useState(Date.now())
  const {
    title,
    inputs,
    values = {},
    titleOkay = 'Okay',
    titleCancel,
    childLevel = 1,
    formClassName = DEFAULT_FORM_CLASS_NAME,
    showHistory,
  } = options
  const [formInputs, setFormInputs] = useState<Record<string, InputType>>(() => buildFormState(inputs, values))

  const openedThisSession = useRef(false)

  useEffect(() => {
    setLastUpdatedAt(Date.now())
  }, [show])

  useEffect(() => {
    if (show) {
      if (!openedThisSession.current) {
        setFormInputs(buildFormState(inputs, values))
        openedThisSession.current = true
      }
    } else {
      openedThisSession.current = false
    }
  }, [show, inputs, values])

  const onChange = (key: string, value: string) => {
    const next = cloneDeep(formInputs)
    value = InputConvert(next[key], value)
    next[key].error = InputValidate({ ...next[key], value })
    next[key].value = value
    setFormInputs(next)
  }

  const handleOk = () => {
    const next = cloneDeep(formInputs)
    const out: Record<string, any> = {}
    let hasError = false
    for (const key of Object.keys(next)) {
      next[key].error = InputValidate(next[key])
      out[key] = next[key].value
      if (next[key].error) hasError = true
    }
    setFormInputs(next)
    if (hasError) return
    proceed(out as any)
  }

  return (
    <Modal
      button={<span></span>}
      title={title || 'Form'}
      titleOkay={titleOkay}
      titleCancel={titleCancel}
      init={false}
      isOpen={show}
      lastUpdatedAt={lastUpdatedAt}
      onOk={handleOk}
      onClose={() => proceed(false as any)}
      childLevel={childLevel}
    >
      <div className={formClassName}>
        {Object.keys(formInputs).map((key) => {
          const input = formInputs[key]

          if (
            input.visible === false ||
            (input.visible && typeof input.visible == 'function' && !input.visible(formInputs))
          )
            return null

          return (
            <div className={`input md:col-span-${input.span || 1}`} key={key}>
              <RenderInput
                input={input}
                Key={key}
                onChange={onChange}
                showHistory={showHistory ? () => showHistory(key) : undefined}
              />
            </div>
          )
        })}
      </div>
    </Modal>
  )
}

const inputsFormDlg = confirmable(InputsFormDialog)

const createInputsFormDlg = createConfirmation(inputsFormDlg) as any as (
  props: any,
) => Promise<Record<string, any> | false>

export const openInputsFormDlg = (options: IInputsFormOption) => createInputsFormDlg({ options })
